import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/lib/analytics";
import { telemetry } from "@/lib/telemetry";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { chatWithAgent, getChatHistory } from "@/api/agents";
import type { StreamEventType, ChatTokenEvent, ChatCompleteEvent } from "@/types/api";
import { Send, Bot, User, Loader2 } from "lucide-react";
import { renderMarkdown } from "@/lib/render-markdown";
import { cn } from "@/lib/utils";

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

interface FollowUpChatProps {
  projectId: string;
  agentId: string;
  executionId?: string;
  isComplete: boolean;
  /** When true, the component fills its parent's height instead of using a fixed scroll height. */
  fill?: boolean;
}

export function FollowUpChat({ projectId, agentId, executionId, isComplete, fill = false }: FollowUpChatProps) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRequestRef = useRef<{
    startedAt: number;
    messageLength: number;
    terminal: boolean;
  } | null>(null);

  const finalizeChatRequest = (
    outcome: "completed" | "failed",
    properties?: Record<string, unknown>,
  ) => {
    const activeRequest = activeRequestRef.current;
    if (!activeRequest || activeRequest.terminal) {
      return;
    }

    activeRequest.terminal = true;
    trackEvent(`agent_chat_${outcome}`, {
      project_id: projectId,
      agent_id: agentId,
      execution_id: executionId,
      duration_ms: Date.now() - activeRequest.startedAt,
      message_length: activeRequest.messageLength,
      ...properties,
    });
  };

  // Load persisted chat history on mount (restores messages after tab switch)
  useEffect(() => {
    if (!isComplete || !projectId || !agentId) return;

    let cancelled = false;
    setIsLoadingHistory(true);

    getChatHistory(projectId, agentId, executionId)
      .then((history) => {
        if (cancelled || history.length === 0) return;
        const restored: ChatMsg[] = history.map((m) => ({
          role: m.role === "human" ? "user" as const : "assistant" as const,
          content: m.content,
        }));
        trackEvent("agent_chat_history_loaded", {
          project_id: projectId,
          agent_id: agentId,
          execution_id: executionId,
          message_count: history.length,
        });
        setMessages(restored);
      })
      .catch((err) => {
        telemetry.captureWarning("chat", "Failed to load chat history", {
          projectId, agentId, executionId,
          error: err instanceof Error ? err.message : String(err),
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoadingHistory(false);
      });

    return () => { cancelled = true; };
  }, [projectId, agentId, executionId, isComplete]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Aborta request em voo + limpa o timeout no unmount (evita timer órfão
  // setando estado em componente desmontado).
  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (!isComplete) return null;

  const handleSend = () => {
    if (!input.trim() || isTyping) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setIsTyping(true);

    // Build history for the API
    const history = messages.map(m => ({
      role: m.role === "user" ? "human" as const : "ai" as const,
      content: m.content,
    }));

    let assistantContent = "";
    activeRequestRef.current = {
      startedAt: Date.now(),
      messageLength: userMsg.length,
      terminal: false,
    };

    // Timeout de inatividade: se ficar 75s sem nenhum token/evento (modelo
    // lento ou throttle do Bedrock), aborta e avisa em vez de fiar pra sempre.
    // Resetado a cada token, então resposta longa mas fluindo não é cortada.
    let timedOut = false;
    const clearChatTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    const armChatTimeout = () => {
      clearChatTimeout();
      timeoutRef.current = setTimeout(() => {
        timedOut = true;
        controllerRef.current?.abort();
        finalizeChatRequest("failed", { error: "timeout" });
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          const note = t(
            "The response took longer than expected (slow model or throttling). Try again.",
          );
          if (last?.role === "assistant" && !last.content) {
            updated[updated.length - 1] = { ...last, content: note };
          } else {
            updated.push({ role: "assistant", content: note });
          }
          return updated;
        });
        setIsTyping(false);
      }, 75000);
    };
    armChatTimeout();
    trackEvent("agent_chat_sent", {
      project_id: projectId,
      agent_id: agentId,
      execution_id: executionId,
      message_length: userMsg.length,
      history_count: history.length,
    });

    controllerRef.current = chatWithAgent(projectId, agentId, {
      message: userMsg,
      execution_id: executionId,
      history,
    }, {
      onEvent: (event: StreamEventType, data: unknown) => {
        if (timedOut) return;
        if (event === "token") {
          armChatTimeout(); // atividade: reseta o relógio de inatividade
          const d = data as ChatTokenEvent;
          assistantContent += d.token;
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === "assistant") {
              updated[updated.length - 1] = { ...last, content: assistantContent };
            } else {
              updated.push({ role: "assistant", content: assistantContent });
            }
            return updated;
          });
        } else if (event === "complete") {
          clearChatTimeout();
          const d = data as ChatCompleteEvent;
          finalizeChatRequest("completed", {
            response_length: (d.content || assistantContent).length,
          });
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === "assistant") {
              updated[updated.length - 1] = { ...last, content: d.content || assistantContent };
            } else {
              updated.push({ role: "assistant", content: d.content || assistantContent });
            }
            return updated;
          });
          setIsTyping(false);
        } else if (event === "error") {
          clearChatTimeout();
          const d = data as { error: string };
          finalizeChatRequest("failed", { error: d.error });
          setMessages(prev => [...prev, { role: "assistant", content: `${t("Error")}: ${d.error}` }]);
          setIsTyping(false);
        }
      },
      onError: (err) => {
        if (timedOut) return; // já tratado pelo timeout (o abort dispara onError)
        clearChatTimeout();
        finalizeChatRequest("failed", { error: err.message });
        setMessages(prev => [...prev, { role: "assistant", content: `${t("Error")}: ${err.message}` }]);
        setIsTyping(false);
      },
      onDone: () => {
        if (timedOut) return;
        clearChatTimeout();
        if (assistantContent) {
          finalizeChatRequest("completed", { response_length: assistantContent.length });
        }
        setIsTyping(false);
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={cn("rounded-lg border bg-card", fill && "flex flex-col h-full overflow-hidden")}>
      <div className="px-3 py-2 border-b flex-shrink-0">
        <span className="text-xs font-medium text-muted-foreground">{t("Follow-up Chat")}</span>
      </div>
      {/* O viewport do radix usa um wrapper display:table que encolhe pro
          conteúdo (não pra largura do painel), então o texto não quebrava e
          clipava. Forçar `block` faz o conteúdo ocupar 100% e quebrar certo;
          tabelas rolam no próprio wrapper (overflow-x-auto no render-markdown). */}
      <ScrollArea
        className={cn(
          "p-3 [&>[data-radix-scroll-area-viewport]>div]:!block",
          fill ? "flex-1 min-h-0" : "h-[250px]",
        )}
        ref={scrollRef}
      >
        {isLoadingHistory && messages.length === 0 && (
          <div className="text-center py-8">
            <Loader2 className="h-6 w-6 text-muted-foreground mx-auto mb-2 animate-spin" />
            <p className="text-xs text-muted-foreground">{t("Loading chat history...")}</p>
          </div>
        )}
        {!isLoadingHistory && messages.length === 0 && (
          <div className="text-center py-8">
            <Bot className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">{t("Ask questions about the analysis results")}</p>
          </div>
        )}
        <div className="space-y-3">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : ""}`}>
              {msg.role === "assistant" && (
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Bot className="h-3 w-3 text-primary" />
                </div>
              )}
              <div className={`min-w-0 max-w-[85%] break-words rounded-lg px-3 py-2 text-xs ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground"
              }`}>
                {msg.role === "assistant" ? renderMarkdown(msg.content) : msg.content}
              </div>
              {msg.role === "user" && (
                <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <User className="h-3 w-3 text-muted-foreground" />
                </div>
              )}
            </div>
          ))}
          {isTyping && messages[messages.length - 1]?.role !== "assistant" && (
            <div className="flex gap-2">
              <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Bot className="h-3 w-3 text-primary" />
              </div>
              <div className="bg-muted rounded-lg px-3 py-2 text-xs">
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: "300ms" }} />
                </span>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
      <div className="p-2 border-t flex gap-2 flex-shrink-0">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("Ask a follow-up question...")}
          className="min-h-[36px] max-h-[72px] text-xs resize-none"
          rows={1}
        />
        <Button size="icon" className="h-9 w-9 shrink-0" onClick={handleSend} disabled={!input.trim() || isTyping}>
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
